import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/Container";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { products } from "@/data/products";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLanguage } from "@/contexts/LanguageContext";
import { possTranslations } from "@/data/poss-translations";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const product = products.find((p) => p.id === productId);
  const isPOSS = productId === "POSS";

  useEffect(() => {
    if (isPOSS) {
      setLanguage('en');
    } else {
      setLanguage('ru');
    }
  }, [isPOSS, setLanguage]);

  const t = isPOSS ? possTranslations.en : possTranslations.ru;

  usePageTitle(product?.name || "Продукт не найден");

  if (!product) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-20">
          <Container>
            <div className="py-32 text-center">
              <h1 className="text-3xl font-bold mb-6">Продукт не найден</h1>
              <Link to="/products" className="button-primary">
                Вернуться к списку продуктов
              </Link>
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-secondary">
          <Container>
            <Link to="/products" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t.backToProducts}
            </Link>
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-12 items-center">
              <div>
                <div className="tag mb-6 animate-fade-in">{t.ourProducts}</div>
                <h1 className="heading text-4xl md:text-5xl font-semibold mb-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                  {product.name} <span className="text-gradient">{product.title}</span>
                </h1>
                <p className="text-muted-foreground text-lg mb-8 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                  {product.description}
                </p>
                <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 animate-fade-in" style={{ animationDelay: "0.6s" }}>
                  <Link to="/contact" className="button-primary">
                    {t.requestDemo}
                  </Link>
                  {product.presentation && (
                    <button onClick={() => window.open(product.presentation, "_blank")} className="button-secondary">
                      {t.downloadPresentation}
                    </button>
                  )}
                </div>
              </div>
              {/* <div className="relative animate-fade-in" style={{ animationDelay: "0.4s" }}>
                <div className="relative rounded-2xl overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
              </div> */}
            </div>
          </Container>
        </section>

        {/* Features Section */}
        <section className="py-20">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="heading text-3xl font-bold mb-8">
                  {t.keyFeatures}
                </h2>
                <div className="space-y-4">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="h-6 w-6 text-primary mr-3 mt-0.5 shrink-0" />
                      <p className="text-foreground/80">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="heading text-3xl font-bold mb-8">
                  {t.benefits}
                </h2>
                <div className="space-y-4">
                  {product.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="h-6 w-6 text-primary mr-3 mt-0.5 shrink-0" />
                      <p className="text-foreground/80">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Container>
        </section>
        {/* Screenshots Section */}
        {/* <section className="py-20 bg-secondary">
          <Container>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="heading text-3xl font-bold mb-6">
                Как это выглядит
              </h2>
              <p className="text-muted-foreground">
                Интуитивно понятный интерфейс, разработанный с учетом потребностей пользователей и лучших практик UX/UI дизайна
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {product.screenshots.map((screenshot, index) => (
                <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] hover:scale-105 transition-transform duration-300 glass rounded-lg overflow-hidden">
                  <div className="h-[32px] w-[2px] bg-gray-800 dark:bg-gray-800 absolute -start-[17px] top-[72px] rounded-s-lg"></div>
                  <div className="h-[46px] w-[2px] bg-gray-800 dark:bg-gray-800 absolute -start-[17px] top-[124px] rounded-s-lg"></div>
                  <div className="h-[46px] w-[2px] bg-gray-800 dark:bg-gray-800 absolute -start-[17px] top-[178px] rounded-s-lg"></div>
                  <div className="h-[64px] w-[2px] bg-gray-800 dark:bg-gray-800 absolute -end-[17px] top-[142px] rounded-e-lg"></div>

                  <div key={index} className="rounded-[2rem] overflow-hidden w-[272px] h-[572px] bg-white dark:bg-gray-800">
                    <img
                      src={screenshot}
                      className="w-[272px] h-[572px]"
                      alt={`Скриншот ${index + 1}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section> */}

        {/* Use Cases Section */}
        <section className="py-20">
          <Container>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="heading text-3xl font-bold mb-6">
                {t.forWhom}
              </h2>
              <p className="text-muted-foreground">
                {t.forWhomDescription}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {product.useCases.map((useCase, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl border border-border">
                  <h3 className="text-xl font-semibold mb-4">{useCase.title}</h3>
                  <p className="text-muted-foreground">{useCase.description}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-secondary">
          <Container>
            <div className="glass rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="heading text-3xl font-bold mb-4">
                    {t.readyToTry}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    {t.readyToTryDescription}
                  </p>
                  <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                    <Link to="/contact" className="button-primary">
                      {t.requestDemoCTA}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                    <Link to="/contact" className="button-secondary">
                      {t.contactUs}
                    </Link>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-square max-w-xs mx-auto bg-white/5 rounded-2xl overflow-hidden p-6">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1512428559087-560fa5ceab42?q=80&w=2070')] bg-cover bg-center opacity-10 rounded-2xl"></div>
                    <div className="relative h-full flex flex-col items-center justify-center text-center space-y-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                          <path d="M9 11h6"></path>
                          <path d="M12 8v6"></path>
                        </svg>
                      </div>
                      <h3 className="text-2xl font-semibold">{t.freeConsultation}</h3>
                      <p className="text-muted-foreground">
                        {t.freeConsultationDescription}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
